// The desktop shell's command surface.
//
// This replaces `window.electronAPI`, which the preload script exposed on the
// window object. Two differences worth knowing:
//
//  * There is no generic escape hatch. The Electron preload also exposed a
//    `window.ipcRenderer` with `invoke` on any channel; nothing used it, and
//    Tauri's capability model would not allow its equivalent anyway.
//  * Every call throws in the web build rather than being silently undefined,
//    so guard with `isDesktop()` at the call site.

import { invoke } from "@tauri-apps/api/core";
import { isDesktop } from "./desktop";

const command = <T>(
	name: string,
	args?: Record<string, unknown>,
): Promise<T> => {
	if (!isDesktop()) {
		return Promise.reject(
			new Error(`${name} is only available in the desktop app`),
		);
	}
	return invoke<T>(name, args);
};

/** Printers the OS knows about, for the settings picker. */
export const listPrinters = () => command<PrinterInfo[]>("list_printers");

/** Renders a receipt to ESC/POS and sends it to `printerName` as a RAW job. */
export const printReceipt = (printerName: string, doc: ReceiptDoc) =>
	command<void>("print_receipt", { printerName, doc });

/** Serial ports, for the label printer picker. */
export const listSerialPorts = () =>
	command<SerialPortInfo[]>("list_serial_ports");

/**
 * Writes EPL2 barcode labels down `port`.
 *
 * The whole batch goes in one call: the printer has no flow control, so the
 * pacing between labels is handled on the Rust side rather than by sleeping in
 * the renderer between individual calls.
 */
export const printBarcodeLabels = (port: string, labels: BarcodeLabel[]) =>
	command<void>("print_barcode_labels", { port, labels });
